var App = App || {};

App.Helpers = (function () {
    'use strict';

    // Wraps tables in the given context with a responsive div if not already wrapped.
    const responsiveTables = (context) => {
        if (!context) return;

        const wrapperClass = "table-responsive";
        const tables = context.querySelectorAll("table");

        tables.forEach(table => {
            if (table._responsiveWrapped || table.parentElement?.classList.contains(wrapperClass)) {
                table._responsiveWrapped = true;
                return;
            }
            const wrapper = document.createElement("div");
            wrapper.className = wrapperClass;
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
            table._responsiveWrapped = true;
        });
    };

    // Checks if the provided href uses a known URI scheme (e.g., javascript:, mailto:, tel:)
    const isUriSchema = (href) => {
        const uriSchemes = ["javascript:", "mailto:", "tel:"];
        for (var i in uriSchemes) {
            if (href.indexOf(uriSchemes[i]) > -1) {
                return true;
            }
        }
        return false;
    }

    // Retrieves the value of a URL query parameter by name
    const getUrlParameter = (name) => {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        const results = regex.exec(location.search);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    };

    // Smoothly scrolls the page to the specified element
    const scrollToElement = (element) => {
        const top = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    const initializeRoutingCopy = (component) => {
        const copyButton = component.querySelector('[data-copy-routing]');
        const routingNumberElement = component.querySelector('[data-routing-number]');

        if (!copyButton || !routingNumberElement) {
            return;
        }

        // WCAG 4.1.3: announce the result through a polite status region; changing the button's
        // aria-label isn't reliably announced and would replace its visible name (2.5.3)
        const status = document.createElement('span');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        copyButton.insertAdjacentElement('afterend', status);

        const announce = (message) => {
            status.textContent = '';
            setTimeout(() => { status.textContent = message; }, 50);
        };

        copyButton.addEventListener('click', async () => {
            const textToCopy = routingNumberElement.textContent.trim();
            if (!textToCopy) {
                return;
            }

            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(textToCopy);
                } else {
                    fallbackCopy(textToCopy);
                }

                copyButton.classList.remove('fa-copy-failed');
                copyButton.classList.add('fa-copy-success');
                announce('Routing number copied');
            } catch (_error) {
                copyButton.classList.add('fa-copy-failed');
                announce('Unable to copy routing number');
            }
        });
    };

    const fallbackCopy = (text) => {
        const hiddenInput = document.createElement('textarea');
        hiddenInput.value = text;
        hiddenInput.setAttribute('readonly', 'readonly');
        hiddenInput.style.position = 'absolute';
        hiddenInput.style.left = '-9999px';
        document.body.appendChild(hiddenInput);
        hiddenInput.select();
        document.execCommand('copy');
        document.body.removeChild(hiddenInput);
    };

    // Splide does not load in PageBuilder (and perhaps other scenarios)
    const splideFallback = (carousel, options, setupFn, fallbackClass = 'splide--fallback') => {

        let mounted = false;
        let instance;

        try {
            instance = new Splide(carousel, options);
            instance.on('mounted', function () { mounted = true; });
            if (typeof setupFn === 'function') setupFn(instance);
            instance.mount();
        }
        catch (e) {
            console.error('Splide init error:', e);
        }

        // Fallback if not mounted in 1 second
        setTimeout(function () {
            if (!mounted) {
                carousel.classList.add(fallbackClass);
                console.warn('Splide failed, fallback applied');
            }
        }, 1000);

        return instance;
    };

    // WCAG 3.2.5 / G201: tell screen reader users when a link opens a new tab. Adds visually hidden text, or extends
    // an existing aria-label (which would otherwise hide that text), unless the link already says so.
    const NEW_WINDOW_TEXT = '(opens in a new tab)';
    const markNewWindowLinks = (context) => {
        if (!context) return;

        context.querySelectorAll('a[target="_blank" i]').forEach(link => {
            if (link.dataset.newWindowMarked) return;
            link.dataset.newWindowMarked = 'true';

            const ariaLabel = link.getAttribute('aria-label');
            const currentName = ariaLabel || link.textContent;
            if (/new (tab|window)/i.test(currentName)) return;

            if (ariaLabel && ariaLabel.trim()) {
                link.setAttribute('aria-label', `${ariaLabel.trim()} ${NEW_WINDOW_TEXT}`);
                return;
            }

            const hint = document.createElement('span');
            hint.className = 'visually-hidden';
            hint.textContent = ` ${NEW_WINDOW_TEXT}`;
            link.appendChild(hint);
        });
    };

    return {
        markNewWindowLinks,
        responsiveTables,
        isUriSchema,
        getUrlParameter,
        scrollToElement,
        splideFallback,
        initializeRoutingCopy
    };

})();